/**
 * **three の舞台を 1 枚**（Issue #523）。層（盤・オブジェクト・演出）ごとにこれを 1 つ置く。
 *
 * - `three` 本体は**動的 import**（`/duel-editor` を開いたときだけ読む。別チャンク）
 * - **時計を持たない**（`purity.test.js`）。描くのは渡された `parts` だけで、
 *   毎フレーム親から新しい `parts` が来る
 * - WebGL が使えない環境（jsdom のテスト・古いブラウザ）では**何も描かないだけ**で落ちない
 *   （`data-three="error"` が付く）
 *
 * カメラ（`shot`）は `stage/camera.js` のショット＝**three のカメラの数値そのもの**なので、
 * ここは変換せずそのまま渡す。同じショットを全部の層に渡すので、層をまたいで絵が一致する。
 */
import { useEffect, useRef, useState } from 'react';
import './ThreeStage.css';

/**
 * @param post 後処理（`{ bloom, threshold, radius, chroma }`。Issue #549）。演出の層だけが渡す
 * @param scale 描き先の解像度の倍率（プレビューを軽くする。既定 1。`runtime.setQuality`）
 */
export default function ThreeStage({ canvas, shot, plane, parts = [], post = null, testId = null, scale = 1 }) {
  const ref = useRef(null);
  const rtRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    import('./three/runtime').then((mod) => {
      if (!alive || !ref.current) return;
      try {
        rtRef.current = mod.createRuntime(ref.current, canvas);
        setReady(true);
      } catch (e) {
        setError(String(e?.message ?? e));
      }
    }).catch((e) => { if (alive) setError(String(e?.message ?? e)); });
    return () => {
      alive = false;
      rtRef.current?.dispose();
      rtRef.current = null;
      setReady(false);
    };
    // 舞台の大きさが変わったら作り直す
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas.w, canvas.h]);

  useEffect(() => {
    const rt = rtRef.current;
    if (!ready || !rt) return;
    rt.setQuality?.(scale);
    rt.setCamera(shot);
    rt.setPost?.(post);
    // **描画の失敗はここで受ける**（投げると effect ごと落ちてページが真っ白になる）
    const why = rt.sync(parts, plane);
    if (why) setError((prev) => prev ?? why);
  }, [ready, shot, plane, parts, post, scale]);

  return (
    <>
      <canvas
        ref={ref}
        className="de-gl"
        width={canvas.w}
        height={canvas.h}
        style={{ width: canvas.w, height: canvas.h }}
        data-three={error ? 'error' : (ready ? 'ready' : 'loading')}
        data-testid={testId ?? undefined}
      />
      {/* 黙って消えると原因が分からないので、理由を舞台に出す */}
      {error ? <div className="de-gl__error" data-testid={testId ? `${testId}-error` : undefined}>{error}</div> : null}
    </>
  );
}
